import PersonAddIcon from "@mui/icons-material/PersonAdd";
import SearchIcon from "@mui/icons-material/Search";
import {
  Box,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Select,
  TextField,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import useIsMobile from "../Hooks/useIsMobile";
import apiService from "../Services/api.service";
import CustomButton from "../components/Button";
import CustomCardList from "../components/Cards";
import PageLoader from "../components/Loader";
import Title from "../components/PageTitle";
import CustomTable from "../components/Table";
import { useToast } from "../components/Toast";

const DoctorsTable = () => {
  const [doctors, setDoctors] = useState([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState(searchParams.get("q") || "");
  const [filteredDoctors, setFilteredDoctors] = useState([]);
  const [page, setPage] = useState(Number(searchParams.get("page")) || 1);
  const [rowsPerPage, setRowsPerPage] = useState(
    Number(searchParams.get("limit")) || 10,
  );
  const [totalCount, setTotalCount] = useState(0); // Total number of doctors (for pagination)
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useIsMobile();
  const [Loader, isLoader] = useState(false);
  const Toast = useToast();
  const auth = localStorage.getItem("token");
  const [typingTimeout, setTypingTimeout] = useState(null);
  const updateURLParams = (newFilters) => {
    setSearchParams({
      q: newFilters.searchQuery || "",
      page: newFilters.page || 1,
      limit: newFilters.rowsPerPage || 10,
    });
  };
  useEffect(() => {
    updateURLParams({
      searchQuery,
      page,
      rowsPerPage,
    });
  }, [searchQuery, page, rowsPerPage]);
  useEffect(() => {
    getDoctors(page, rowsPerPage); // Fetch data on page or rowsPerPage change
  }, [page, rowsPerPage]); // Add dependencies

  const getDoctors = async (page, limit) => {
    isLoader(true);
    try {
      const { mappedDoctors, count } = await apiService.fetchDoctors(
        auth,
        page,
        limit,
      );
      setDoctors(mappedDoctors);
      setFilteredDoctors(mappedDoctors);

      setTotalCount(count); // Set the total count
      isLoader(false);
    } catch (error) {
      Toast.error("Error while Fetching");
    } finally {
      isLoader(false);
    }
  };
  useEffect(() => {
    if (typingTimeout) clearTimeout(typingTimeout);

    const timeout = setTimeout(async () => {
      try {
        const { mappedDoctors, count } = await apiService.fetchDoctors(
          auth,
          page,
          10,
          searchQuery,
        );
        setTotalCount(count);
        setFilteredDoctors(mappedDoctors);
      } catch (error) {
        console.error("Error fetching Kiosk:", error);
        Toast.error("Error while Fetching");
      }
    }, 500); // 500ms debounce

    setTypingTimeout(timeout);

    // Cleanup
    return () => clearTimeout(timeout);
  }, [searchQuery, page]);
  const handleSearch = (event) => {
    const keyword = event.target.value.toLowerCase();
    setSearchQuery(keyword); // Only set the query
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage + 1); // Set the new page
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10)); // Update rows per page when changed
    setPage(1); // Reset to page 1 when rows per page change
  };
  const formatDate = (dateString) => {
    if (dateString == "NA" || dateString == "Invalid Date") return "NA";
    const [year, month, day] = dateString.split("-");
    return `${day}-${month}-${year}`;
  };

  const handleCardClick = (doctor) => {
    if (location.pathname === "/dashboard/doctor") {
      navigate(`/dashboard/doctor/doctorDetails/${doctor.id}`, {
        state: {
          doctorId: doctor.id,
          doctorName: doctor.name,
          docMobile: doctor.mobile,
          username: doctor.username,
        },
      });
    } else if (location.pathname === "/dashboard/engage") {
      navigate(`/dashboard/engage/doctorDetails/${doctor.id}`, {
        state: {
          doctorId: doctor.id,
          doctorName: doctor.name,
        },
      });
    }
  };
  const handleRowClick = (row) => {
    if (location.pathname === "/dashboard/doctor") {
      navigate(`/dashboard/doctor/doctorDetails/${row.id}`, {
        state: {
          doctorId: row.id,
          doctorName: row.name,
          docMobile: row.mobile,
          username: row.username,
        },
      });
    } else if (location.pathname === "/dashboard/engage") {
      navigate(`/dashboard/engage/doctorDetails/${row.id}`, {
        state: {
          doctorId: row.id,
          doctorName: row.name,
        },
      });
    }
  };
  const handleEngageAccess = async (doctor, engageAccess) => {
    const formData = new FormData();
    formData.append(
      "doctor",
      JSON.stringify({
        _id: doctor.id,
        engageAccess: engageAccess,
      }),
    );

    try {
      await apiService.updateDoctor(formData, auth);
      Toast.success(`Doctor updated successfully`);
      window.location.reload();
    } catch (error) {
      Toast.error("Failed to update EEngage access");
      console.error("Update error:", error);
    }
  };
  const handleToggleAccess = async (doctor) => {
    const updatedStatus =
      doctor.inactivity === "enabled" ? "disabled" : "enabled";

    const formData = new FormData();
    formData.append(
      "doctor",
      JSON.stringify({
        _id: doctor.id,
        status: updatedStatus,
      }),
    );

    try {
      await apiService.updateDoctor(formData, auth);
      Toast.success(
        `Doctor ${
          updatedStatus === "enabled" ? "enabled" : "disabled"
        } successfully`,
      );
      window.location.reload();
    } catch (error) {
      Toast.error("Failed to update access");
      console.error("Update error:", error);
    }
  };
  const columns = [
    { field: "name", headerName: "Name", align: "center" },
    { field: "mobile", headerName: "Mobile", align: "center" },
    {
      field: "engageAccess",
      headerName: "Engage Access",
      subHeader: "To disable access change it to none",
      align: "center",
      renderCell: (value, row) => (
        <Select
          value={row.engageAccess ?? "none"}
          onChange={(e) => handleEngageAccess(row, e.target.value)}
          size="small"
          style={{ width: 150 }}
        >
          <MenuItem value="admin">Admin</MenuItem>
          <MenuItem value="receptionist">Receptionist</MenuItem>
          <MenuItem value="none">None</MenuItem>
        </Select>
      ),
    },
  ];

  // Conditionally push the "Access" column
  // if (location.pathname === "/dashboard/doctor") {
  //   columns.push({
  //     field: "inactivity",
  //     headerName: "Status",
  //     align: "center",
  //     renderCell: (value, row) => (
  //       <Switch
  //         checked={!row.inactivity || row.inactivity === "enabled"}
  //         onChange={() => handleToggleAccess(row)}
  //         color="primary"
  //       />
  //     ),
  //   });
  // }
  // if (location.pathname === "/dashboard/engage") {
  //   columns.push({
  //     field: "engageAccess",
  //     headerName: "Engage Access",
  //     align: "center",
  //     renderCell: (value, row) => (
  //       <Select
  //         value={row.engageAccess ?? "none"}
  //         onChange={(e) => handleEngageAccess(row, e.target.value)}
  //         size="small"
  //         style={{ width: 150 }}
  //       >
  //         <MenuItem value="admin">Admin</MenuItem>
  //         <MenuItem value="receptionist">Receptionist</MenuItem>
  //         <MenuItem value="none">None</MenuItem>
  //       </Select>
  //     ),
  //   });
  // }

  return (
    <div>
      <Paper sx={{ padding: 2 }}>
        <Title
          text="Doctors"
          showActions={true}
          actions={[
            {
              label: "Add Doctor ",
              onClick: () => navigate("/dashboard/doctor/add-doctor"),
            },
          ]}
        />
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
            marginBottom: 2,
          }}
        >
          <TextField
            label="Search"
            variant="outlined"
            value={searchQuery}
            onChange={handleSearch}
            fullWidth
            sx={{ marginBottom: 2, marginTop: 3 }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton>
                    <SearchIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Box>

        {isMobile ? (
          <CustomCardList
            fields={[
              { label: "Mobile", key: "mobile" },
              { label: "Username", key: "username" },
            ]}
            data={filteredDoctors}
            onCardClick={handleCardClick}
            page={page - 1} // Adjust for zero-based indexing for `CustomTable`
            rowsPerPage={rowsPerPage}
            totalCount={totalCount}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleRowsPerPageChange}
          />
        ) : (
          <CustomTable
            columns={columns}
            data={filteredDoctors}
            actions={[
              {
                Component: CustomButton,
                props: { text: "Details", width: "90px", height: "40px" },
                onClick: handleRowClick,
              },
            ]}
            page={page - 1} // Adjust for zero-based indexing for `CustomTable`
            rowsPerPage={rowsPerPage}
            totalCount={totalCount}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleRowsPerPageChange}
          />
        )}
      </Paper>
      {Loader && <PageLoader />}
    </div>
  );
};

export default DoctorsTable;
